import { Link } from 'react-router-dom'
import { formatDateRange, formatVenueTime } from '../lib/datetime.js'
import { excerpt } from '../lib/text.js'
import { conferenceTags } from '../lib/grouping.js'
import { isPastEvent } from '../lib/dateRange.js'
import { placeHref, tagHref, yearHref } from '../lib/permalinks.js'
import useCurrentYear from '../hooks/useCurrentYear.js'
import PlaceLink from './PlaceLink.jsx'
import TalkRow from './TalkRow.jsx'
import TagPill from './TagPill.jsx'
import PastPill from './PastPill.jsx'
import { BTN_PRIMARY } from '../lib/ui.js'

// #308: a conference is a container, so its card is shaped differently from a
// talk's. The blue rail carries the conference's DATE RANGE (Sep 23–24) rather
// than one timestamp, and the body lists the talks inside it — including talks
// by other Actian speakers, not just the viewer's own.
//
// Standalone talks keep rendering through EventCard, unchanged. EventList picks
// between the two.

export default function ConferenceCard({ conference }) {
  const { weekday, monthDay } = formatDateRange(conference.startsAtUtc, conference.endsAtUtc)
  const location = [conference.city, conference.region, conference.country].filter(Boolean).join(', ')
  const talks = conference.talks || []
  const preview = excerpt(conference.abstractText)
  const detailHref = `/conferences/${conference.id}`
  // #334: the tags come from the talks — the nested ConferenceSummaryDto
  // carries none, so `conference.tags` alone rendered nothing at all.
  const tags = conferenceTags(conference)
  // A conference is over once its END has passed; using the start would mark
  // day two of a two-day event as past while it is still running.
  const past = isPastEvent({ startsAtUtc: conference.endsAtUtc || conference.startsAtUtc })
  const year = useCurrentYear()
  const place = placeHref(conference, year)

  return (
    <article className="group relative overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/60 transition hover:shadow-lg hover:ring-brand-200 sm:hover:-translate-y-0.5">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-500 to-brand-300 opacity-0 transition group-hover:opacity-100" />

      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:p-6">
        {/* Same fixed width as EventCard's rail (#311) so conference and talk
            cards line up down a mixed column. */}
        {/* #338: same as EventCard — the date rail is the year's permalink,
            lifted above the card's stretched title link. */}
        <Link
          to={yearHref(conference.startsAtUtc) || '/'}
          title={`All events in ${new Date(conference.startsAtUtc).getUTCFullYear()}`}
          className={`relative z-10 flex items-start gap-3 transition sm:w-[128px] sm:shrink-0 sm:flex-col sm:items-center sm:justify-center sm:gap-1 sm:rounded-xl sm:px-4 sm:py-3 ${
            past ? 'sm:bg-slate-100 sm:hover:bg-slate-200' : 'sm:bg-brand-50/60 sm:hover:bg-brand-100/70'
          }`}
        >
          <div className={`whitespace-nowrap text-xs font-medium uppercase tracking-wider ${
            past ? 'text-slate-500' : 'text-brand-700'
          }`}>
            {weekday}
          </div>
          <div className="whitespace-nowrap text-xl font-bold leading-tight text-slate-900 sm:text-2xl">
            {monthDay}
          </div>
          <div className="whitespace-nowrap text-xs text-slate-600 sm:mt-1">
            {talks.length === 1 ? '1 talk' : `${talks.length} talks`}
          </div>
        </Link>

        <div className="min-w-0 flex-1">
          <div className="relative z-10 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800">
              conference
            </span>
            {past ? <PastPill /> : null}
            {conference.format ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700">
                {conference.format.toLowerCase().replace('_', '-')}
              </span>
            ) : null}
            {tags.slice(0, 4).map((t) => (
              <TagPill key={t.slug || t.name} tag={t} href={tagHref(t, year)} />
            ))}
            {tags.length > 4 ? (
              <span className="text-xs text-slate-500">+{tags.length - 4}</span>
            ) : null}
          </div>

          <h2 className="mt-2 text-lg font-semibold leading-snug tracking-tight text-slate-900 transition group-hover:text-brand-700 sm:text-xl">
            {/* Stretched link, same pattern as EventCard: the hit area covers
                the card, while the talk links and action buttons sit above it
                on `relative z-10`. */}
            <Link to={detailHref} className="static before:absolute before:inset-0 before:content-['']">
              {conference.name}
            </Link>
          </h2>

          {conference.venue ? (
            <div className="mt-0.5 text-sm text-slate-500">{conference.venue}</div>
          ) : null}

          {preview ? (
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{preview}</p>
          ) : null}

          {talks.length ? (
            <ul className="mt-3">
              {talks.map((t) => (
                <TalkRow key={t.id} talk={t} year={year} />
              ))}
            </ul>
          ) : (
            /* A private, invitation-only conference publishes its container and
               nothing else. Say that plainly rather than implying there is no
               programme — there usually is one, it just is not public. */
            <p className="mt-3 text-sm italic text-slate-500">No talks listed publicly.</p>
          )}

          <div className="relative z-10 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            {location ? (
              <PlaceLink href={place}>{location}</PlaceLink>
            ) : null}
            {conference.tzId ? (
              <span className="text-xs text-slate-500">
                Venue local: {formatVenueTime(conference.startsAtUtc, conference.tzId)} ({conference.tzId})
              </span>
            ) : null}
          </div>

          {conference.registrationUrl ? (
            <div className="relative z-10 mt-4 flex flex-wrap items-center gap-2">
              <a
                href={conference.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={BTN_PRIMARY}
              >
                Register ↗
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  )
}
